import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { supabase } from '../../config/supabase';
import MasterItemEditModal from './MasterItemEditModal';
import './MasterItemManagerPage.css';

/**
 * MasterItemManagerPage - Unified admin page for managing master items + bundles.
 * Creates/edits items atomically via manage_master_item RPC.
 * Access via /admin/item-manager.
 */
const MasterItemManagerPage = () => {
  // Data state
  const [masterItems, setMasterItems] = useState([]);
  const [itemTypes, setItemTypes] = useState([]);
  const [costTypes, setCostTypes] = useState([]);
  const [phases, setPhases] = useState([]);
  const [vendorTypes, setVendorTypes] = useState([]);
  const [bbChapters, setBbChapters] = useState([]);
  const [spaces, setSpaces] = useState([]);
  const [constructionTypes, setConstructionTypes] = useState([]);
  const [suggestedItems, setSuggestedItems] = useState({});
  const [homeChoiceItems, setHomeChoiceItems] = useState({});
  const [loading, setLoading] = useState(true);

  // UI state
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [editingItem, setEditingItem] = useState(null); // null = closed, {} = new item
  const [editIsBundle, setEditIsBundle] = useState(false);

  // ─── Data fetching ───────────────────────────────────────────────

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [
        itemsRes,
        itemTypesRes,
        costTypesRes,
        phasesRes,
        vendorTypesRes,
        chaptersRes,
        spacesRes,
        constructionTypesRes,
        suggestedRes,
        homeChoiceRes,
      ] = await Promise.all([
        supabase
          .from('master_items')
          .select(
            'id, item_name, item_type_id, cost_type_id, project_phase_id, estimated_cost, notes, bb_chapter_id, vendor_type_id, include_in_boq, requires_gc_prep, is_bundle, bundle_type, bundle_items, item_options, boq_items',
          )
          .order('item_name'),
        supabase.from('reference_item_types').select('id, name').order('display_order'),
        supabase.from('reference_cost_types').select('id, name').order('display_order'),
        supabase.from('reference_project_phases').select('id, name').order('display_order'),
        supabase
          .from('reference_vendor_types')
          .select('id, name, category, display_order')
          .order('display_order'),
        supabase
          .from('reference_bb_chapters')
          .select('id, code, name_he, friendly_name, depth, parent_id')
          .order('code'),
        supabase.from('master_spaces').select('id, name').order('name'),
        supabase.from('reference_construction_types').select('id, name'),
        supabase.from('reference_suggested_items').select('*'),
        supabase.from('reference_home_choice_items').select('*'),
      ]);

      setMasterItems(itemsRes.data || []);
      setItemTypes(itemTypesRes.data || []);
      setCostTypes(costTypesRes.data || []);
      setPhases(phasesRes.data || []);
      setVendorTypes(vendorTypesRes.data || []);
      setBbChapters(chaptersRes.data || []);
      setSpaces(spacesRes.data || []);
      setConstructionTypes(constructionTypesRes.data || []);

      // Index suggested items by master_item_id
      const sugMap = {};
      (suggestedRes.data || []).forEach(s => {
        if (!sugMap[s.master_item_id]) sugMap[s.master_item_id] = [];
        sugMap[s.master_item_id].push(s);
      });
      setSuggestedItems(sugMap);

      // Index home choice items by master_item_id
      const hcMap = {};
      (homeChoiceRes.data || []).forEach(hc => {
        hcMap[hc.master_item_id] = hc;
      });
      setHomeChoiceItems(hcMap);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ─── Lookup maps ─────────────────────────────────────────────────

  const itemTypeMap = useMemo(() => {
    const m = {};
    itemTypes.forEach(t => {
      m[t.id] = t.name;
    });
    return m;
  }, [itemTypes]);

  const vendorTypeMap = useMemo(() => {
    const m = {};
    vendorTypes.forEach(t => {
      m[t.id] = t.name;
    });
    return m;
  }, [vendorTypes]);

  const bbChapterMap = useMemo(() => {
    const m = {};
    bbChapters.forEach(ch => {
      m[ch.id] = ch.friendly_name || ch.name_he;
    });
    return m;
  }, [bbChapters]);

  // ─── Filtering ───────────────────────────────────────────────────

  const filteredItems = useMemo(() => {
    let items = masterItems;

    // Text search
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      items = items.filter(i => i.item_name.toLowerCase().includes(term));
    }

    // Filter
    switch (filter) {
      case 'items-only':
        items = items.filter(i => !i.is_bundle);
        break;
      case 'bundles-only':
        items = items.filter(i => i.is_bundle);
        break;
      case 'has-bb':
        items = items.filter(i => i.bb_chapter_id);
        break;
      case 'no-bb':
        items = items.filter(i => !i.bb_chapter_id);
        break;
      case 'has-vendor':
        items = items.filter(i => i.vendor_type_id);
        break;
      case 'no-vendor':
        items = items.filter(i => !i.vendor_type_id);
        break;
      case 'home-choice':
        items = items.filter(i => homeChoiceItems[i.id]);
        break;
      case 'no-spaces':
        items = items.filter(i => !suggestedItems[i.id] || suggestedItems[i.id].length === 0);
        break;
      default:
        break;
    }

    return items;
  }, [masterItems, searchTerm, filter, homeChoiceItems, suggestedItems]);

  // ─── Stats ───────────────────────────────────────────────────────

  const stats = useMemo(() => {
    const total = masterItems.length;
    const withBb = masterItems.filter(i => i.bb_chapter_id).length;
    const withVendor = masterItems.filter(i => i.vendor_type_id).length;
    const bundles = masterItems.filter(i => i.is_bundle).length;
    return { total, withBb, withVendor, bundles };
  }, [masterItems]);

  // ─── Save handler ────────────────────────────────────────────────

  const handleSave = async rpcData => {
    const { data, error } = await supabase.rpc('manage_master_item', rpcData);
    if (error) throw error;
    setEditingItem(null);
    await fetchData();
    return data;
  };

  // ─── Render ──────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="item-manager-page">
        <div className="item-manager-loading">טוען נתונים...</div>
      </div>
    );
  }

  const filters = [
    { id: 'all', label: 'הכל' },
    { id: 'items-only', label: 'פריטים' },
    { id: 'bundles-only', label: 'חבילות' },
    { id: 'has-bb', label: 'עם BB' },
    { id: 'no-bb', label: 'ללא BB' },
    { id: 'has-vendor', label: 'עם ספק' },
    { id: 'no-vendor', label: 'ללא ספק' },
    { id: 'home-choice', label: 'בחירות בית' },
    { id: 'no-spaces', label: 'ללא חללים' },
  ];

  return (
    <div className="item-manager-page">
      {/* Header */}
      <div className="item-manager-header">
        <h1>ניהול פריטי מאסטר</h1>
        <div className="item-manager-header-actions">
          <input
            type="text"
            className="item-manager-search"
            placeholder="חפש פריט..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
          <button
            className="item-manager-btn primary"
            onClick={() => {
              setEditIsBundle(false);
              setEditingItem({});
            }}
          >
            + פריט חדש
          </button>
          <button
            className="item-manager-btn primary"
            onClick={() => {
              setEditIsBundle(true);
              setEditingItem({});
            }}
          >
            + חבילה חדשה
          </button>
        </div>
      </div>

      {/* Progress */}
      <div className="item-manager-progress">
        <div className="item-manager-progress-bar">
          <div
            className="item-manager-progress-fill"
            style={{
              width: `${stats.total > 0 ? Math.round((stats.withBb / stats.total) * 100) : 0}%`,
            }}
          />
        </div>
        <div className="item-manager-progress-text">
          {stats.total} פריטים | {stats.withBb} עם BB | {stats.withVendor} עם ספק | {stats.bundles}{' '}
          חבילות | מוצג: {filteredItems.length}
        </div>
      </div>

      {/* Filters */}
      <div className="item-manager-filters">
        {filters.map(f => (
          <button
            key={f.id}
            className={`item-manager-filter-btn ${filter === f.id ? 'active' : ''}`}
            onClick={() => setFilter(f.id)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="item-manager-table-container">
        <table className="item-manager-table">
          <thead>
            <tr>
              <th>#</th>
              <th>שם</th>
              <th>סוג</th>
              <th>סוג פריט</th>
              <th>סוג ספק</th>
              <th>פרק BB</th>
              <th>חללים</th>
              <th>בית</th>
              <th>BOQ</th>
            </tr>
          </thead>
          <tbody>
            {filteredItems.length === 0 && (
              <tr>
                <td colSpan={9} className="item-manager-empty">
                  אין פריטים תואמים
                </td>
              </tr>
            )}
            {filteredItems.map((item, idx) => (
              <tr
                key={item.id}
                onClick={() => {
                  setEditIsBundle(item.is_bundle);
                  setEditingItem(item);
                }}
              >
                <td>{idx + 1}</td>
                <td>{item.item_name}</td>
                <td>
                  <span className={`item-manager-badge ${item.is_bundle ? 'bundle' : 'item'}`}>
                    {item.is_bundle ? (item.bundle_type === 'choice' ? 'בחירה' : 'חבילה') : 'פריט'}
                  </span>
                </td>
                <td>{itemTypeMap[item.item_type_id] || '—'}</td>
                <td>{vendorTypeMap[item.vendor_type_id] || '—'}</td>
                <td>{bbChapterMap[item.bb_chapter_id] || '—'}</td>
                <td>{suggestedItems[item.id]?.length || '—'}</td>
                <td>
                  <span
                    className={`item-manager-badge ${homeChoiceItems[item.id] ? 'check' : 'dash'}`}
                  >
                    {homeChoiceItems[item.id] ? '✓' : '—'}
                  </span>
                </td>
                <td>
                  <span className={`item-manager-badge ${item.include_in_boq ? 'check' : 'dash'}`}>
                    {item.include_in_boq ? '✓' : '—'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Edit Modal */}
      {editingItem && (
        <MasterItemEditModal
          item={editingItem.id ? editingItem : null}
          isBundle={editIsBundle}
          allItems={masterItems}
          spaceAssignments={editingItem.id ? suggestedItems[editingItem.id] || [] : []}
          homeChoiceConfig={editingItem.id ? homeChoiceItems[editingItem.id] || null : null}
          referenceData={{
            itemTypes,
            costTypes,
            phases,
            vendorTypes,
            bbChapters,
            spaces,
            constructionTypes,
          }}
          onSave={handleSave}
          onClose={() => setEditingItem(null)}
        />
      )}
    </div>
  );
};

export default MasterItemManagerPage;
